import React, { useState, useCallback } from "react";
import {
    Box,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Typography,
    TextField,
    Button,
    IconButton,
    Rating,
    Snackbar,
    Alert,
    Fade,
    Divider
} from "@mui/material";
import {
    Dismiss24Regular,
    Star24Filled,
    Star24Regular,
    ChatBubblesQuestion24Regular,
    Send24Regular,
    Sparkle24Regular
} from "@fluentui/react-icons";
import { logEvent } from "../../helper/AppInsightsHelper";

// Theme colors - matching CloseTheLoopPage
const THEME = {
    primary: "#0078D4",
    primaryLight: "#2B88D8",
    primaryDark: "#005A9E",
    background: "#F5F7FA",
    surface: "#FFFFFF",
    textPrimary: "#1A1A2E",
    textSecondary: "#5A5A72",
    border: "#E1E5EB",
    success: "#107C10",
    warning: "#FFB900",
    error: "#D13438",
    gradient: "linear-gradient(135deg, #0078D4 0%, #005A9E 100%)"
};

interface FeedbackFormModalProps {
    isOpen: boolean;
    onClose: () => void;
}

interface FeedbackData {
    overallExperience: number | null;
    whatCanBeImproved: string;
    whatWentWell: string;
    howMuchDidItHelp: number | null;
}

const FeedbackFormModal: React.FC<FeedbackFormModalProps> = ({ isOpen, onClose }) => {
    const [feedback, setFeedback] = useState<FeedbackData>({
        overallExperience: null,
        whatCanBeImproved: "",
        whatWentWell: "",
        howMuchDidItHelp: null
    });
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [showSuccess, setShowSuccess] = useState(false);
    const [hoverRating, setHoverRating] = useState<{ field: string; value: number | null }>({ field: "", value: null });

    // Get user info from session storage
    const userName = sessionStorage.getItem("loginName") || "Unknown User";
    const userEmail = sessionStorage.getItem("loginUserName") || "unknown@microsoft.com";

    const handleRatingChange = useCallback((field: keyof FeedbackData, value: number | null) => {
        setFeedback(prev => ({ ...prev, [field]: value }));
    }, []);

    const handleTextChange = useCallback((field: keyof FeedbackData) => (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        setFeedback(prev => ({ ...prev, [field]: event.target.value }));
    }, []);

    const handleSubmit = useCallback(async () => {
        setIsSubmitting(true);

        try {
            // Prepare feedback payload
            const feedbackPayload = {
                ...feedback,
                userName,
                userEmail,
                submittedAt: new Date().toISOString(),
                page: "CloseTheLoop",
                userAgent: navigator.userAgent
            };

            // Log to Application Insights
            logEvent("CTL_FeedbackSubmitted", "CloseTheLoop", "FeedbackFormModal", feedbackPayload);

            // Also log to console for local development testing
            if (window.location.origin.includes("localhost")) {
                console.info("📝 Feedback Submitted (Local Dev Mode):", feedbackPayload);
            }

            // Simulate slight delay for UX
            await new Promise(resolve => setTimeout(resolve, 500));

            // Reset form and show success
            setFeedback({
                overallExperience: null,
                whatCanBeImproved: "",
                whatWentWell: "",
                howMuchDidItHelp: null
            });
            setShowSuccess(true);

            // Close modal after showing success
            setTimeout(() => {
                onClose();
            }, 1500);

        } catch (error) {
            console.error("Failed to submit feedback:", error);
        } finally {
            setIsSubmitting(false);
        }
    }, [feedback, userName, userEmail, onClose]);

    const isFormValid = feedback.overallExperience !== null || feedback.howMuchDidItHelp !== null ||
        feedback.whatCanBeImproved.trim() !== "" || feedback.whatWentWell.trim() !== "";

    const getRatingLabel = (value: number | null, hoverValue: number | null): string => {
        const activeValue = hoverValue ?? value;
        if (activeValue === null) return "";
        const labels = ["Poor", "Fair", "Good", "Very Good", "Excellent"];
        return labels[activeValue - 1] || "";
    };

    const getHelpfulnessLabel = (value: number | null, hoverValue: number | null): string => {
        const activeValue = hoverValue ?? value;
        if (activeValue === null) return "";
        const labels = ["Not at all", "A little", "Moderately", "Very helpful", "Extremely helpful"];
        return labels[activeValue - 1] || "";
    };

    return (
        <>
            <Dialog
                open={isOpen}
                onClose={onClose}
                maxWidth="sm"
                fullWidth
                TransitionComponent={Fade}
                transitionDuration={200}
                PaperProps={{
                    sx: {
                        borderRadius: "16px",
                        overflow: "hidden",
                        boxShadow: "0 24px 48px rgba(0,0,0,0.2)"
                    }
                }}
            >
                {/* Gradient Header */}
                <Box
                    sx={{
                        background: THEME.gradient,
                        p: 3,
                        position: "relative"
                    }}
                >
                    <IconButton
                        onClick={onClose}
                        sx={{
                            position: "absolute",
                            right: 12,
                            top: 12,
                            color: "#FFFFFF",
                            "&:hover": {
                                backgroundColor: "rgba(255,255,255,0.15)"
                            }
                        }}
                    >
                        <Dismiss24Regular />
                    </IconButton>
                    <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                        <Box
                            sx={{
                                width: 48,
                                height: 48,
                                borderRadius: "12px",
                                backgroundColor: "rgba(255,255,255,0.2)",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center"
                            }}
                        >
                            <ChatBubblesQuestion24Regular style={{ color: "#FFFFFF", fontSize: 24 }} />
                        </Box>
                        <Box>
                            <Typography variant="h6" sx={{ color: "#FFFFFF", fontWeight: 600 }}>
                                Share Your Feedback
                            </Typography>
                            <Typography variant="body2" sx={{ color: "rgba(255,255,255,0.85)", mt: 0.25 }}>
                                Help us improve Close the Loop
                            </Typography>
                        </Box>
                    </Box>
                </Box>

                {/* User Info Banner */}
                <Box
                    sx={{
                        px: 3,
                        py: 1.5,
                        backgroundColor: "#F9FAFB",
                        borderBottom: `1px solid ${THEME.border}`,
                        display: "flex",
                        alignItems: "center",
                        gap: 1
                    }}
                >
                    <Sparkle24Regular style={{ color: THEME.primary, fontSize: 18 }} />
                    <Typography variant="body2" sx={{ color: THEME.textSecondary }}>
                        Submitting as <strong style={{ color: THEME.textPrimary }}>{userName}</strong>
                    </Typography>
                </Box>

                <DialogContent sx={{ p: 0 }}>
                    <Box sx={{ p: 3, display: "flex", flexDirection: "column", gap: 3.5 }}>
                        {/* Question 1: Overall Experience */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: THEME.textPrimary, mb: 1.5 }}
                            >
                                1. How would you rate your overall experience?
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Rating
                                    value={feedback.overallExperience}
                                    onChange={(_, value) => handleRatingChange("overallExperience", value)}
                                    onChangeActive={(_, value) => setHoverRating({ field: "overallExperience", value })}
                                    size="large"
                                    icon={<Star24Filled style={{ color: "#FFB900", fontSize: 32 }} />}
                                    emptyIcon={<Star24Regular style={{ color: THEME.border, fontSize: 32 }} />}
                                    sx={{
                                        "& .MuiRating-icon": {
                                            cursor: "pointer",
                                            transition: "transform 0.15s ease",
                                            "&:hover": {
                                                transform: "scale(1.15)"
                                            }
                                        }
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: THEME.primary,
                                        fontWeight: 500,
                                        minWidth: 100
                                    }}
                                >
                                    {getRatingLabel(
                                        feedback.overallExperience,
                                        hoverRating.field === "overallExperience" ? hoverRating.value : null
                                    )}
                                </Typography>
                            </Box>
                        </Box>

                        <Divider sx={{ borderColor: THEME.border }} />

                        {/* Question 2: What can be improved */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: THEME.textPrimary, mb: 1.5 }}
                            >
                                2. What can be improved?
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Please share specific areas where we can improve..."
                                value={feedback.whatCanBeImproved}
                                onChange={handleTextChange("whatCanBeImproved")}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        backgroundColor: "#FAFBFC",
                                        "&:hover fieldset": {
                                            borderColor: THEME.primary
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: THEME.primary,
                                            borderWidth: 2
                                        }
                                    }
                                }}
                            />
                        </Box>

                        {/* Question 3: What went well */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: THEME.textPrimary, mb: 1.5 }}
                            >
                                3. What went well?
                            </Typography>
                            <TextField
                                multiline
                                rows={3}
                                fullWidth
                                placeholder="Tell us what you liked about the experience..."
                                value={feedback.whatWentWell}
                                onChange={handleTextChange("whatWentWell")}
                                sx={{
                                    "& .MuiOutlinedInput-root": {
                                        borderRadius: "10px",
                                        fontSize: "14px",
                                        backgroundColor: "#FAFBFC",
                                        "&:hover fieldset": {
                                            borderColor: THEME.primary
                                        },
                                        "&.Mui-focused fieldset": {
                                            borderColor: THEME.primary,
                                            borderWidth: 2
                                        }
                                    }
                                }}
                            />
                        </Box>

                        <Divider sx={{ borderColor: THEME.border }} />

                        {/* Question 4: How much did it help */}
                        <Box>
                            <Typography
                                variant="subtitle1"
                                sx={{ fontWeight: 600, color: THEME.textPrimary, mb: 1.5 }}
                            >
                                4. How much did this app help you?
                            </Typography>
                            <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                                <Rating
                                    value={feedback.howMuchDidItHelp}
                                    onChange={(_, value) => handleRatingChange("howMuchDidItHelp", value)}
                                    onChangeActive={(_, value) => setHoverRating({ field: "howMuchDidItHelp", value })}
                                    size="large"
                                    icon={<Star24Filled style={{ color: "#107C10", fontSize: 32 }} />}
                                    emptyIcon={<Star24Regular style={{ color: THEME.border, fontSize: 32 }} />}
                                    sx={{
                                        "& .MuiRating-icon": {
                                            cursor: "pointer",
                                            transition: "transform 0.15s ease",
                                            "&:hover": {
                                                transform: "scale(1.15)"
                                            }
                                        }
                                    }}
                                />
                                <Typography
                                    variant="body2"
                                    sx={{
                                        color: THEME.success,
                                        fontWeight: 500,
                                        minWidth: 120
                                    }}
                                >
                                    {getHelpfulnessLabel(
                                        feedback.howMuchDidItHelp,
                                        hoverRating.field === "howMuchDidItHelp" ? hoverRating.value : null
                                    )}
                                </Typography>
                            </Box>
                            <Box sx={{ display: "flex", justifyContent: "space-between", mt: 0.5, px: 0.5 }}>
                                <Typography variant="caption" sx={{ color: THEME.textSecondary }}>
                                    Not at all
                                </Typography>
                                <Typography variant="caption" sx={{ color: THEME.textSecondary }}>
                                    Very much
                                </Typography>
                            </Box>
                        </Box>
                    </Box>
                </DialogContent>

                <DialogActions
                    sx={{
                        p: 3,
                        pt: 2,
                        borderTop: `1px solid ${THEME.border}`,
                        backgroundColor: "#FAFBFC"
                    }}
                >
                    <Button
                        onClick={onClose}
                        sx={{
                            color: THEME.textSecondary,
                            textTransform: "none",
                            fontWeight: 500,
                            px: 3,
                            "&:hover": {
                                backgroundColor: "#F0F0F0"
                            }
                        }}
                    >
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={handleSubmit}
                        disabled={!isFormValid || isSubmitting}
                        startIcon={<Send24Regular />}
                        sx={{
                            background: THEME.gradient,
                            textTransform: "none",
                            fontWeight: 600,
                            px: 4,
                            py: 1.25,
                            borderRadius: "8px",
                            boxShadow: "0 4px 12px rgba(0,120,212,0.3)",
                            "&:hover": {
                                background: "linear-gradient(135deg, #005A9E 0%, #004578 100%)",
                                boxShadow: "0 6px 16px rgba(0,120,212,0.4)"
                            },
                            "&:disabled": {
                                background: "#E1E5EB",
                                color: "#9E9E9E"
                            }
                        }}
                    >
                        {isSubmitting ? "Submitting..." : "Submit Feedback"}
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Success Snackbar */}
            <Snackbar
                open={showSuccess}
                autoHideDuration={2000}
                onClose={() => setShowSuccess(false)}
                anchorOrigin={{ vertical: "top", horizontal: "center" }}
            >
                <Alert
                    severity="success"
                    sx={{
                        borderRadius: "10px",
                        boxShadow: "0 8px 24px rgba(0,0,0,0.15)",
                        "& .MuiAlert-icon": {
                            fontSize: 24
                        }
                    }}
                >
                    Thank you for your feedback! 🎉
                </Alert>
            </Snackbar>
        </>
    );
};

export default FeedbackFormModal;
